import { Controller } from "@hotwired/stimulus"

// The whole of the composer's formatting: emphasis and strong emphasis, written
// as *this* and **this** in the plain text (Formatting sets them when the page
// is drawn). At a keyboard, ⌘B / ⌘I (Ctrl on Windows) wrap the selection, or
// unwrap it if it is already wrapped. On a phone, where there is no ⌘, a small
// tooltip appears over a selection with the two marks. Nothing else: no
// underline (the dotted line already means an aside), no headings, no lists.
export default class extends Controller {
  static targets = [ "field", "tooltip" ]

  connect() {
    this.onSelection = () => this.place()
    this.onScroll = () => this.hide()
    document.addEventListener("selectionchange", this.onSelection)
    window.addEventListener("scroll", this.onScroll, { passive: true })
  }

  disconnect() {
    document.removeEventListener("selectionchange", this.onSelection)
    window.removeEventListener("scroll", this.onScroll)
  }

  keydown(event) {
    if (!(event.metaKey || event.ctrlKey) || event.altKey) return
    const key = event.key.toLowerCase()
    if (key === "b") { event.preventDefault(); this.wrap("**") }
    else if (key === "i") { event.preventDefault(); this.wrap("*") }
  }

  strong() { this.wrap("**") }
  em() { this.wrap("*") }

  // Wraps the selection in the mark, or takes the mark off a selection that
  // has it (inside or just outside the selection). The field's own undo
  // history is kept by writing through execCommand where it exists.
  wrap(mark) {
    const field = this.fieldTarget
    let start = field.selectionStart
    let end = field.selectionEnd
    if (start === end) return
    const value = field.value

    // Leading and trailing spaces stay outside the marks.
    while (start < end && /\s/.test(value[start])) start++
    while (end > start && /\s/.test(value[end - 1])) end--
    if (start === end) return

    const inner = value.slice(start, end)
    const has = (text) => text.startsWith(mark) && text.endsWith(mark) && text.length >= mark.length * 2 + 1
    let replacement, from, to

    if (has(inner)) {
      replacement = inner.slice(mark.length, inner.length - mark.length)
      from = start; to = end
    } else if (value.slice(start - mark.length, start) === mark && value.slice(end, end + mark.length) === mark) {
      replacement = inner
      from = start - mark.length; to = end + mark.length
    } else {
      replacement = `${mark}${inner}${mark}`
      from = start; to = end
    }

    field.focus()
    field.setSelectionRange(from, to)
    if (!document.execCommand || !document.execCommand("insertText", false, replacement)) {
      field.setRangeText(replacement, from, to, "select")
    }
    const selected = replacement.startsWith(mark) ? [ from + mark.length, from + replacement.length - mark.length ] : [ from, from + replacement.length ]
    field.setSelectionRange(selected[0], selected[1])
    field.dispatchEvent(new Event("input", { bubbles: true }))
    this.hide()
  }

  // The tooltip, on a touch screen only: over the selection, near its end,
  // where the thumb just lifted. A mouse and keyboard have ⌘B and ⌘I.
  place() {
    if (!this.hasTooltipTarget || !this.hasFieldTarget) return
    if (!window.matchMedia("(pointer: coarse)").matches) return
    const field = this.fieldTarget
    if (document.activeElement !== field || field.selectionStart === field.selectionEnd) { this.hide(); return }

    const rect = field.getBoundingClientRect()
    const host = this.element.getBoundingClientRect()
    const caret = this.caretPosition(field, field.selectionEnd)
    const tip = this.tooltipTarget
    tip.hidden = false
    const left = Math.min(Math.max(rect.left - host.left + caret.left - tip.offsetWidth / 2, 8), host.width - tip.offsetWidth - 8)
    const top = rect.top - host.top + caret.top - tip.offsetHeight - 12
    tip.style.left = `${left}px`
    tip.style.top = `${Math.max(top, 4)}px`
  }

  hide() {
    if (this.hasTooltipTarget) this.tooltipTarget.hidden = true
  }

  // Where a character is in the textarea, by laying the same text out in a
  // twin the reader never sees. Good enough to put a tooltip near.
  caretPosition(field, index) {
    const twin = document.createElement("div")
    const style = window.getComputedStyle(field)
    for (const name of [ "fontFamily", "fontSize", "fontWeight", "lineHeight", "letterSpacing", "padding", "border", "boxSizing", "whiteSpace", "wordWrap", "overflowWrap" ]) {
      twin.style[name] = style[name]
    }
    twin.style.position = "absolute"
    twin.style.visibility = "hidden"
    twin.style.whiteSpace = "pre-wrap"
    twin.style.width = `${field.clientWidth}px`
    twin.textContent = field.value.slice(0, index)
    const marker = document.createElement("span")
    marker.textContent = "\u200b"
    twin.append(marker)
    document.body.append(twin)
    const position = { left: marker.offsetLeft, top: marker.offsetTop - field.scrollTop }
    twin.remove()
    return position
  }
}
